<template>
  <div class="text-field-date-picker">
    <v-menu
      :offset="[20, 10]"
      :close-on-content-click="false"
      transition="scale-transition"
      min-width="290px"
    >
      <template #activator="{ props }">
        <div v-if="disabled">
          <v-text-field
            v-model="dateDisplay"
            :label="label"
            prepend-inner-icon="$calendar"
            disabled
            variant="outlined"
            density="compact"
            :placeholder="$t('datePickerStrings.placeholders.date')"
          />
        </div>
        <div v-else>
          <vee-field
            v-slot="{ errors, componentField }"
            v-model="textDate"
            rules="date"
            :name="label"
          >
            <v-text-field
              v-bind="componentField"
              :label="label"
              :error-messages="errors"
              variant="outlined"
              density="compact"
              :placeholder="$t('datePickerStrings.placeholders.date')"
              @blur="updateFromText"
            >
              <template #prepend-inner>
                <v-icon
                  v-bind="props"
                  icon="$calendar"
                />
              </template>
            </v-text-field>
          </vee-field>
        </div>
      </template>
      <v-date-picker
        v-model="internalDate"
        :allowed-dates="allowedDates"
        :disabled="disabled"
        :first-day-of-week="1"
        @input="update"
      />
    </v-menu>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { Field as VeeField } from "vee-validate";
import { m } from '@/plugins/moment';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  modelValue: String,
  label: String,
  allowedDates: Function,
  disabled: Boolean
});

const textDate = ref("");
const internalDate = ref("");

const emit = defineEmits(["update:modelValue"]);

const update = () => {
  textDate.value = dateDisplay.value;
  emit("update:modelValue", dateValue.value);
};

const updateFromText = () => {
  if (!textDate.value) {
    emit("update:modelValue", "");
    return;
  }

  const date = m(
    textDate.value,
    t("commonStrings.dateDisplayFormat")
  );

  if (!date.isValid()) {
    return;
  }

  const objTime = {
    year: date.year(),
    month: date.month(),
    date: date.date(),
    hour: 0,
    minute: 0
  };

  if (props.modelValue) {
    const initial = m(
      props.modelValue,
      t("commonStrings.dateValueFormat")
    );

    objTime.hour = initial.hour();
    objTime.minute = initial.minute();
  }

  const newDate = m(objTime);

  internalDate.value = newDate.format(
    t("commonStrings.dateValueFormat")
  );
  textDate.value = dateDisplay.value;
  emit("update:modelValue", dateValue.value);
};

const dateDisplay = computed(() => {
  if (!internalDate.value) {
    return null;
  }

  return m(
    internalDate.value,
    t("commonStrings.dateValueFormat")
  ).format(t("commonStrings.dateDisplayFormat"));
});

const dateValue = computed(() => {
  const date = m(
    internalDate.value,
    t("commonStrings.dateValueFormat")
  );
  const objTime = {
    year: date.year(),
    month: date.month(),
    date: date.date()
  };

  if (props.modelValue) {
    const initial = m(
      props.modelValue,
      t("commonStrings.dateValueFormat")
    );

    objTime.hour = initial.hour();
    objTime.minute = initial.minute();
  }

  const newDate = m(objTime);

  return newDate.isValid() ?
  newDate.format(t("commonStrings.dateValueFormat"))
    :
  "";
});

onMounted(() => {
  if (props.modelValue) {
    internalDate.value = m(
      props.modelValue,
      t("commonStrings.dateValueFormat")
    ).format(t("commonStrings.dateValueFormat"));
    textDate.value = dateDisplay.value;
  }
});

watch(() => props.modelValue,
  value => {
    if (value) {
      internalDate.value = m(
        value,
        t("commonStrings.dateValueFormat")
      ).format(t("commonStrings.dateValueFormat"));
      textDate.value = dateDisplay.value;
    } else {
      internalDate.value = null;
      textDate.value = "";
      emit("update:modelValue", dateValue.value);
    }
});

watch(() => dateDisplay.value,
  value => {
    textDate.value = value;
    emit("update:modelValue", dateValue.value);
});

</script>
